import React, { useEffect, useState, useRef } from "react";
import axios from "../api/axiosInstance";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  zIndex: 22000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px 12px",
};

const shellStyle = {
  width: "100%",
  maxWidth: "520px",
  borderRadius: "16px",
  padding: "28px",
  background: "var(--erp-modal-bg, #0e1730)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
  borderTop: "3px solid #3DB6E9",
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100dvh - 36px)",
  overflowY: "auto",
};

const contentWrapperStyle = {};

const sectionStyle = {
  marginTop: "18px",
  borderRadius: "16px",
  padding: "18px",
  background: "var(--erp-modal-section, #121f3d)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.05rem",
  fontWeight: 800,
  color: "var(--erp-modal-title, #f2f6ff)",
};

const sectionTextStyle = {
  margin: "4px 0 0",
  fontSize: "0.78rem",
  color: "var(--erp-modal-muted, #aebbd0)",
};

const fieldGridStyle = {
  marginTop: "12px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
};

const labelStyle = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--erp-modal-text, #dfe8f7)",
  marginBottom: "4px",
  display: "block",
};

const inputFieldStyle = {
  width: "100%",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "var(--erp-modal-input, #152344)",
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  color: "var(--erp-modal-text, #dfe8f7)",
  fontSize: "0.95rem",
};

const actionsRowStyle = {
  marginTop: "16px",
  display: "flex",
  gap: "12px",
};

const primaryButtonStyle = {
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  flex: 1,
  border: "1px solid var(--erp-modal-primary-border, #3DB6E9)",
  background: "var(--erp-modal-primary, #223A7A)",
  color: "#ffffff",
};

const secondaryButtonStyle = {
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  flex: 1,
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  background: "var(--erp-modal-bg, #0e1730)",
  color: "var(--erp-modal-text, #dfe8f7)",
};

const toastOverlayStyle = {
  position: "fixed",
  top: "14px",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 25000,
};

const toastCardStyle = {
  background: "var(--erp-modal-primary, #223A7A)",
  border: "1px solid #3DB6E9",
  borderRadius: "18px",
  padding: "14px 24px",
  color: "#ffffff",
  fontWeight: 700,
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  maxWidth: "420px",
  width: "min(90vw, 420px)",
  textAlign: "center",
};

const savingOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  fontWeight: "bold",
  zIndex: 23000,
  pointerEvents: "none",
};

function toLocalISODate(value) {
  const d = new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildDateRange(startValue, endValue) {
  const start = new Date(`${String(startValue).slice(0, 10)}T00:00:00`);
  const end = new Date(`${String(endValue).slice(0, 10)}T00:00:00`);
  const out = [];

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(toLocalISODate(d));
  }

  return out;
}

function getStopSellDay(stopSell) {
  return String(stopSell?.stay_date || stopSell?.start_date || "").slice(0, 10);
}

export default function AddStopSellPopup({ show, onClose, formData }) {
  const roomRateId = formData?.room_rate?.id || formData?.room_rate_id || null;
  const [startDate, setStartDate] = useState(formData?.start_date || "");
  const [endDate, setEndDate] = useState(formData?.end_date || "");
  const [submitting, setSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimer = useRef(null);

  const todayISO = () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t.toISOString().slice(0, 10);
  };

  useEffect(() => {
    return () => {
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
      }
    };
  }, []);

  if (!show) return null;

  const showToast = (message, closeAfter = true) => {
    if (toastTimer.current) {
      clearTimeout(toastTimer.current);
    }
    setToastMessage(message);
    if (closeAfter) {
      toastTimer.current = setTimeout(() => {
        setToastMessage("");
        onClose && onClose();
        window.location.reload();
      }, 2000);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!roomRateId) {
      alert("Missing room rate.");
      return;
    }
    if (!startDate || !endDate) {
      alert("Start and end date are required.");
      return;
    }
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      alert("Invalid dates.");
      return;
    }
    if (start < new Date(`${todayISO()}T00:00:00`)) {
      alert("Start date cannot be in the past.");
      return;
    }

    const expectedDays = buildDateRange(startDate, endDate);

    const verifyAppliedAfterTimeout = async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/stop-sells`,
        { withCredentials: true }
      );

      const freshStopSells = Array.isArray(data) ? data : [];
      const expectedKeys = new Set(
        expectedDays.map((day) => `${String(roomRateId)}|${day}`)
      );

      const matchedKeys = new Set(
        freshStopSells
          .filter((ss) => String(ss.room_rate_id) === String(roomRateId))
          .map((ss) => `${String(ss.room_rate_id)}|${getStopSellDay(ss)}`)
          .filter((key) => expectedKeys.has(key))
      );

      return matchedKeys.size === expectedKeys.size;
    };

    try {
      setSubmitting(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/stop-sells`,
        {
          room_rate_id: roomRateId,
          start_date: startDate,
          end_date: endDate,
        },
        { withCredentials: true }
      );
      showToast(res?.data?.message || "Stop Sell created.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 504) {
        try {
          const alreadyApplied = await verifyAppliedAfterTimeout();
          if (alreadyApplied) {
            showToast("Stop Sell created.");
            return;
          }
        } catch (verificationError) {
          console.error("Error verifying Stop Sell creation after timeout:", verificationError);
        }
      }
      console.error("Error creating Stop Sell:", err);
      setSubmitting(false);
      alert(err?.response?.data?.error || "Error saving Stop Sell.");
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={shellStyle}>
        <div style={{ textAlign: "center", marginBottom: "18px" }}>
          <p
            style={{
              letterSpacing: "0.35em",
              fontSize: "0.8rem",
              color: "var(--erp-modal-muted, #aebbd0)",
              marginBottom: "6px",
              textTransform: "uppercase",
            }}
          >
            Room rate studio
          </p>
          <h2 style={{ margin: 0, fontSize: "1.35rem", color: "var(--erp-modal-title, #f2f6ff)" }}>Add Stop Sell</h2>
          <p style={{ marginTop: "6px", color: "var(--erp-modal-muted, #aebbd0)" }}>
            Block availability for the selected room rate and date range.
          </p>
        </div>

        <div style={contentWrapperStyle}>
          <form onSubmit={handleSubmit}>
            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Dates</h3>
                  <p style={sectionTextStyle}>
                    Stop sells are applied inclusively. The backend enforces today+ ranges.
                  </p>
                </div>
              </div>
              <div style={fieldGridStyle}>
                <div>
                  <label style={labelStyle}>Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    min={todayISO()}
                    style={inputFieldStyle}
                    onChange={(event) => setStartDate(event.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label style={labelStyle}>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate || todayISO()}
                    style={inputFieldStyle}
                    onChange={(event) => setEndDate(event.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>
            </section>

            <div style={actionsRowStyle}>
              <button type="submit" style={primaryButtonStyle} disabled={submitting}>
                {submitting ? "Saving…" : "Save"}
              </button>
              <button type="button" style={secondaryButtonStyle} onClick={() => onClose && onClose()} disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {submitting && <div style={savingOverlayStyle}>Saving stop sell…</div>}

      {toastMessage && (
        <div style={toastOverlayStyle}>
          <div style={toastCardStyle}>{toastMessage}</div>
        </div>
      )}
    </div>
  );
}
